import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import CommonCoverNoteModal from '../components/wizard/CommonCoverNoteModal';
import {
  loadCaseRequestViewData,
  type CaseRequestViewPayload,
} from '../services/caseRequestViewData';
import { getCommonCoverNote } from '../services/coverNoteRequests';
import * as criminalService from '../services/criminalCaseRequests';
import { coverNoteQrImageSrc } from '../utils/coverNoteQr';
import { printCoverNotePdf } from '../utils/coverNotePrint';
import { setFlashToast } from '../utils/flashToast';
import type { WizardFlowKind } from '../utils/wizardCategoryRoute';
import { usesCommonCoverNote, usesCriminalAppellateCoverNote } from '../utils/wizardCategoryRoute';
import CriminalCoverNoteModal from './wizard/criminal/CriminalCoverNoteModal';
import CriminalAppellateCoverNoteModal from './wizard/criminalAppellate/CriminalAppellateCoverNoteModal';
import RightToInformationCoverNoteModal from './wizard/rightToInformation/RightToInformationCoverNoteModal';
import {
  getRightToInformationCoverNote,
  rightToInformationDraftToCoverNoteFallback,
} from './wizard/rightToInformation/rightToInformationCoverNote';
import ViewRequestCaseDetailsRouter from './view-request/ViewRequestCaseDetailsRouter';
import ViewRequestCommunicationsTab from './view-request/ViewRequestCommunicationsTab';
import ViewRequestDocumentsTab from './view-request/ViewRequestDocumentsTab';
import { apiDisplayValue, statusPillClass } from './view-request/viewRequestHelpers';

type ViewTab = 'cd' | 'docs' | 'comm';

function draftFromPayload(payload: CaseRequestViewPayload | null) {
  return payload?.data.draft ?? null;
}

function statusFromPayload(payload: CaseRequestViewPayload | null, noDataLabel: string): string {
  return payload?.data.statusName?.trim() || noDataLabel;
}

export default function ViewRequestPage() {
  const { t } = useTranslation();
  const { caseRequestId: caseRequestIdParam } = useParams();
  const caseRequestId = decodeURIComponent(caseRequestIdParam ?? '').trim();
  const noData = t('common.noDataFound');

  const [tab, setTab] = useState<ViewTab>('cd');
  const [loading, setLoading] = useState(true);
  const [flow, setFlow] = useState<WizardFlowKind>('unknown');
  const [viewPayload, setViewPayload] = useState<CaseRequestViewPayload | null>(null);
  const [showCoverNote, setShowCoverNote] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [coverNoteQr, setCoverNoteQr] = useState('');
  const [coverNoteLoading, setCoverNoteLoading] = useState(false);
  const [printingCoverNote, setPrintingCoverNote] = useState(false);

  useEffect(() => {
    if (!caseRequestId) {
      setLoading(false);
      setLoadError(t('viewRequest.caseRequestIdMissing'));
      return;
    }

    let mounted = true;
    setLoading(true);
    setLoadError('');
    setViewPayload(null);
    setFlow('unknown');

    void loadCaseRequestViewData(caseRequestId).then((res) => {
      if (!mounted) return;
      setLoading(false);
      if (!res.success) {
        const msg = res.message ?? t('viewRequest.loadFailed');
        setLoadError(msg);
        setFlashToast({ message: msg, variant: 'error' });
        return;
      }
      if (!res.data.payload) {
        setLoadError(t('viewRequest.notFound'));
        return;
      }
      setFlow(res.data.flow);
      setViewPayload(res.data.payload);
    });

    return () => {
      mounted = false;
    };
  }, [caseRequestId, t]);

  const draft = draftFromPayload(viewPayload);
  const qrRequestId = draft?.draftRequestId || caseRequestId;
  const canLoadTabs = Boolean(qrRequestId) && !loading && !loadError && Boolean(viewPayload);

  useEffect(() => {
    if (!qrRequestId || loading || loadError || flow === 'unknown') {
      if (!qrRequestId) setCoverNoteQr('');
      return;
    }

    let mounted = true;
    setCoverNoteLoading(true);
    setCoverNoteQr('');

    const loadQr = async () => {
      if (usesCommonCoverNote(flow) || usesCriminalAppellateCoverNote(flow)) {
        const r = await getCommonCoverNote(qrRequestId);
        if (!mounted) return;
        setCoverNoteLoading(false);
        if (!r.success) return;
        setCoverNoteQr(r.data.qr ?? '');
        return;
      }

      if (flow === 'right-to-information') {
        const r = await getRightToInformationCoverNote(qrRequestId);
        if (!mounted) return;
        setCoverNoteLoading(false);
        if (!r.success) return;
        setCoverNoteQr(r.data.qr ?? '');
        return;
      }

      const r = await criminalService.getCriminalCoverNote(qrRequestId);
      if (!mounted) return;
      setCoverNoteLoading(false);
      if (!r.success) return;
      setCoverNoteQr(r.data.qr ?? '');
    };

    void loadQr();

    return () => {
      mounted = false;
    };
  }, [qrRequestId, flow, loading, loadError]);

  const fileRef = loading
    ? t('viewRequest.loadingCaseRequest')
    : loadError
      ? noData
      : apiDisplayValue(draft?.referenceNo, noData);
  const fileCategory = loading
    ? t('viewRequest.loadingCaseRequest')
    : loadError
      ? noData
      : apiDisplayValue(draft?.fileCategory, noData);
  const subFileCategory = loading
    ? t('viewRequest.loadingCaseRequest')
    : loadError
      ? noData
      : apiDisplayValue(draft?.subFileCategoryName, noData);
  const statusName = loading
    ? t('viewRequest.loadingCaseRequest')
    : loadError
      ? noData
      : statusFromPayload(viewPayload, noData);
  const qrSrc = coverNoteQrImageSrc(coverNoteQr);
  const fileRefForCoverNote = draft?.referenceNo?.trim() || '';
  const rtiCoverNoteFallback = useMemo(() => {
    if (flow !== 'right-to-information' || !viewPayload || viewPayload.flow !== 'right-to-information') {
      return undefined;
    }
    return rightToInformationDraftToCoverNoteFallback(viewPayload.data.draft, {
      fileRef: fileRefForCoverNote,
      submittedAt: viewPayload.data.submittedAt,
      submittedDivisionName: viewPayload.data.draft.submittedDivisionName,
    });
  }, [flow, viewPayload, fileRefForCoverNote]);

  const handlePrintCoverNote = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!qrRequestId || printingCoverNote) return;

    setShowCoverNote(false);
    setPrintingCoverNote(true);
    const r = await printCoverNotePdf(qrRequestId, flow, fileRefForCoverNote || undefined);
    setPrintingCoverNote(false);

    if (!r.success) {
      setFlashToast({ message: r.message ?? t('viewRequest.coverNotePdfFailed'), variant: 'error' });
    }
  };

  const coverNoteModals = (
    <>
      <CriminalCoverNoteModal
        isOpen={showCoverNote && flow === 'criminal'}
        caseRequestId={qrRequestId}
        fileRefFallback={fileRefForCoverNote}
        onClose={() => setShowCoverNote(false)}
      />
      <CommonCoverNoteModal
        isOpen={showCoverNote && usesCommonCoverNote(flow)}
        caseRequestId={qrRequestId}
        fileRefFallback={fileRefForCoverNote}
        onClose={() => setShowCoverNote(false)}
      />
      <CriminalAppellateCoverNoteModal
        isOpen={showCoverNote && usesCriminalAppellateCoverNote(flow)}
        caseRequestId={qrRequestId}
        fileRefFallback={fileRefForCoverNote}
        onClose={() => setShowCoverNote(false)}
      />
      <RightToInformationCoverNoteModal
        isOpen={showCoverNote && flow === 'right-to-information'}
        caseRequestId={qrRequestId}
        fileRefFallback={fileRefForCoverNote}
        coverNoteFallback={rtiCoverNoteFallback}
        onClose={() => setShowCoverNote(false)}
      />
    </>
  );

  return (
    <>
      {coverNoteModals}

      <div className="bc">
        <Link to="/tasks">{t('viewRequest.breadcrumbParent')}</Link>
        <span className="bc-sep">/</span>
        {t('viewRequest.title')}
      </div>

      <div className="pg-title vcr-page-title">{t('viewRequest.title')}</div>

      <div className="vr-meta vcr-meta">
        <div className="vm">
          <label>{t('viewRequest.fileReferenceNumber')}</label>
          <p>{fileRef}</p>
        </div>
        <div className="vm-div" />
        <div className="vm">
          <label>{t('viewRequest.fileCategory')}</label>
          <p>{fileCategory}</p>
        </div>
        <div className="vm-div" />
        <div className="vm">
          <label>{t('viewRequest.subFileCategory')}</label>
          <p>{subFileCategory}</p>
        </div>
        <div className="vm-div" />
        <div className="vm vm-status">
          <label>{t('viewRequest.currentStatus')}</label>
          {!loading && !loadError ? (
            <span className={`vcr-status-pill ${statusPillClass(statusName)}`}>{statusName}</span>
          ) : (
            <p>{statusName}</p>
          )}
        </div>
      </div>

      <div className="vcr-layout">
        <div className="tab-card vcr-tabs">
          <div className="tab-bar">
            <button className={`tab-b ${tab === 'cd' ? 'on' : ''}`} type="button" onClick={() => setTab('cd')}>
              {t('viewRequest.tabCaseDetails')}
            </button>
            <button className={`tab-b ${tab === 'docs' ? 'on' : ''}`} type="button" onClick={() => setTab('docs')}>
              {t('viewRequest.tabDocuments')}
            </button>
            <button className={`tab-b ${tab === 'comm' ? 'on' : ''}`} type="button" onClick={() => setTab('comm')}>
              {t('viewRequest.tabCommunications')}
            </button>
          </div>

          <div className="tab-body vcr-tab-body">
            {tab === 'cd' ? (
              loading ? (
                <p className="vcr-loading">{t('viewRequest.loadingCaseRequest')}</p>
              ) : loadError ? (
                <p className="vcr-error">{loadError}</p>
              ) : viewPayload ? (
                <ViewRequestCaseDetailsRouter payload={viewPayload} caseRequestId={caseRequestId} />
              ) : (
                <p className="vcr-error">{t('viewRequest.notFound')}</p>
              )
            ) : null}
            {tab === 'docs' && canLoadTabs ? (
              <ViewRequestDocumentsTab caseRequestId={qrRequestId} flow={flow} />
            ) : null}
            {tab === 'comm' && canLoadTabs ? (
              <ViewRequestCommunicationsTab caseRequestId={qrRequestId} />
            ) : null}
            {tab !== 'cd' && !canLoadTabs ? (
              <p className="vcr-error">
                {loadError || (loading ? t('viewRequest.loadingCaseRequest') : t('viewRequest.notFound'))}
              </p>
            ) : null}
          </div>
        </div>

        <aside className="vcr-sidebar">
          <div className="qr-panel vcr-qr-panel">
            <div className="qr-img-w">
              {coverNoteLoading ? (
                <div className="vcr-qr-placeholder" aria-busy="true">
                  {t('viewRequest.loadingQr')}
                </div>
              ) : qrSrc ? (
                <div style={{ background: '#fff', padding: 8, borderRadius: 8 }}>
                  <img src={qrSrc} alt={t('viewRequest.qrAlt')} width={120} height={120} />
                </div>
              ) : (
                <div className="vcr-qr-placeholder">{t('viewRequest.qrUnavailable')}</div>
              )}
            </div>
            <p className="qr-tip vcr-qr-tip">
              {t('viewRequest.qrTip')}
            </p>
            <button
              className="qr-ab"
              type="button"
              onClick={() => setShowCoverNote(true)}
              disabled={!qrRequestId || coverNoteLoading || Boolean(loadError)}
            >
              {t('viewRequest.viewCoverNote')}
            </button>
            <button
              className="qr-ab vcr-print-btn"
              type="button"
              onClick={(e) => void handlePrintCoverNote(e)}
              disabled={!qrRequestId || coverNoteLoading || printingCoverNote || Boolean(loadError)}
            >
              {printingCoverNote ? t('viewRequest.preparing') : t('viewRequest.printCoverNote')}
            </button>
          </div>
        </aside>
      </div>
    </>
  );
}
